import { Table, TableColumnType, TableProps } from "antd-v5";
import classNames from "classnames";
import useDimensions from "hooks/useDimensions";
import useSyncState from "hooks/useSyncState";
import { TableCustomizationHelper } from "hooks/useTableCustomizationHelper";
import { has, isFunction, isNumber, isUndefined, noop, omit } from "lodash";
import React, {
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FilterOperationType } from "types/api/filterOperationType";
import { DateFormatter } from "types/common";
import {
  FilterChangeCallBack,
  FilterInfo,
  FilterOperationInfo,
  FilterValue,
  GroupByChangeCallback,
  SortChangeCallBack,
  SortInfo,
  SortOrder,
  SortType,
} from "types/pagination";

import ACTrigger from "ac-components/ACTrigger";
import {
  ACFilterBarItemType,
  ACFilterDropdownProps,
} from "ac-components/ac-filter-bar/ACFilterBar";
import ChevronDownIcon from "ac-components/ac-icons/ChevronDownIcon";
import ChevronRightIcon from "ac-components/ac-icons/ChevronRightIcon";
import FilterIcon from "ac-components/ac-icons/FilterIcon";
import SortAscIcon from "ac-components/ac-icons/SortAscIcon";
import SortDescIcon from "ac-components/ac-icons/SortDescIcon";

import ACColumnOptionDropdown, {
  ACColumnOptionDropdownProps,
} from "./ACColumnOptionDropdown";
import "./ACTable.less";

export type ColumnTitleRenderers<Record> = {
  [key: React.Key]: (column: TableColumnType<Record>) => React.ReactNode;
};

export type ACTableColumnType<Record> = TableColumnType<Record> & {
  sortType?: SortType;
  showDropdown?: boolean;
  draggable?: boolean;
  dateFormatter?: DateFormatter;
  customMenuRender?: (props: ACColumnMenuCommonProps) => ReactNode;
  skipColumnMenu?: boolean;
  allowGroupBy?: ACColumnOptionDropdownProps["allowGroupBy"];
  groupByKey?: string;
  testId?: string;
  filterLabelFormatter?: ACFilterBarItemType["filterLabelFormatter"];
};

export type ACColumnMenuCommonProps = Pick<
  ACColumnOptionDropdownProps,
  "onPopupVisibleChange"
>;

const sortOrderMap: Record<string, SortOrder[]> = {
  number: ["descend", "ascend", null],
  default: ["ascend", "descend", null] as SortOrder[],
};

export interface ACTableProps<Record>
  extends Omit<TableProps<Record>, "size" | "prefixCls" | "columns"> {
  columns?: ACTableColumnType<Record>[];
  sortInfo?: SortInfo;
  onSortChange?: SortChangeCallBack;
  filterInfo?: FilterInfo;
  filterOperationInfo?: FilterOperationInfo;
  onFilterChange?: FilterChangeCallBack;
  groupBy?: string[];
  onGroupByChange?: GroupByChangeCallback;
  computeTableWidth?: boolean;
  autoEnableScroll?: boolean;
  fullPage?: boolean;
  getTableWidth?: (width?: number) => void;
  testId?: string;

  // since we want to keep the title in the column list as string so that searching works properly
  // columnTitleRenderers should be used to render custom titles (eg: tootlip, help text)
  columnTitleRenderers?: ColumnTitleRenderers<Record>;

  rightScrollShadow?: boolean;
  customizationHelpers?: TableCustomizationHelper<Record>;

  footerSize?: "sm" | "default";
  actionRender?: (record: Record) => ReactNode | ReactNode[];
  actionColumnWidth?: number;
}

export default function ACTable<Record extends object>({
  columns,
  pagination,
  rowClassName,
  sortInfo = {},
  onSortChange,
  filterInfo = {},
  filterOperationInfo = {},
  onFilterChange,
  groupBy,
  onGroupByChange,
  onHeaderRow,
  rowSelection,
  computeTableWidth,
  autoEnableScroll,
  expandable,
  columnTitleRenderers,
  fullPage,
  getTableWidth,
  sticky,
  rightScrollShadow,
  customizationHelpers,
  dataSource,
  testId,
  footerSize,
  actionRender,
  actionColumnWidth,
  ...restProps
}: ACTableProps<Record>) {
  const [activeColumnHoverKey, setActiveColumnHoverKey] = useState<string>();
  const [activeFilterKey, setActiveFilterKey] = useState<string>();
  const [getActiveFilterSelectedKeys, setActiveFilterSelectedKeys] =
    useSyncState<FilterValue[]>([]);
  const [getActiveFilterSelectedOperation, setActiveFilterSelectedOperation] =
    useSyncState<FilterOperationType | undefined>(undefined);
  const [visibleMenuKey, setVisibleMenuKey] = useState<string>();

  const _onSortChange = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!isFunction(onSortChange)) {
        return;
      }
      const columnKey = e.currentTarget.dataset.key as string;
      const currentSortType = e.currentTarget.dataset.type as SortOrder;

      const columnSortType = e.currentTarget.dataset.sortType as SortType;
      e.currentTarget.blur();
      const sortOrderArray =
        sortOrderMap[columnSortType] || sortOrderMap["default"];

      // Find the index of the current sort type and calculate the next one
      const currentIndex = sortOrderArray.indexOf(currentSortType);
      const nextSortType =
        sortOrderArray[(currentIndex + 1) % sortOrderArray.length];

      const sortInfo: SortInfo = nextSortType
        ? { [columnKey]: nextSortType }
        : {};
      onSortChange(sortInfo);
    },
    [onSortChange],
  );

  const applyFilter = useCallback(
    (
      key: string,
      selectedValues: FilterValue[] | undefined,
      filterOperation?: FilterOperationType,
      closeMenu?: boolean,
    ) => {
      if (isFunction(onFilterChange)) {
        const _newFilterdInfo = { ...filterInfo };
        if (isUndefined(selectedValues) || selectedValues.length < 1) {
          if (has(_newFilterdInfo, key)) {
            _newFilterdInfo[key] = [];
          } else {
            delete _newFilterdInfo[key];
          }
        } else {
          _newFilterdInfo[key] = selectedValues;
        }
        const _newFilterOperationInfo = { ...filterOperationInfo };
        if (filterOperation) {
          _newFilterOperationInfo[key] = filterOperation;
        } else {
          delete _newFilterOperationInfo[key];
        }
        onFilterChange(_newFilterdInfo, _newFilterOperationInfo);
        if (isUndefined(closeMenu) || closeMenu) {
          setActiveColumnHoverKey(undefined);
          setActiveFilterKey(undefined);
        }
        setActiveFilterSelectedKeys([]);
        setActiveFilterSelectedOperation(undefined);
      }
    },
    [
      filterInfo,
      onFilterChange,
      setActiveFilterSelectedKeys,
      setActiveFilterSelectedOperation,
      filterOperationInfo,
    ],
  );

  let modifiedColumns = useMemo(() => {
    if (!columns) {
      return [];
    }

    const {
      alwaysVisibleKeys,
      columnOrderConfig,
      draggableColumns,
      onColumnOrderChange,
      onColumnVisibilityChange,
    } = customizationHelpers || {};

    return columns.map((column) => {
      const key = column.key as string;
      const title = columnTitleRenderers?.[key]?.(column) ?? column.title;
      const columnSortType = column.sortType;
      if (
        (column.filterDropdown ||
          column.sorter ||
          column.dateFormatter ||
          customizationHelpers ||
          column.customMenuRender) &&
        !column.skipColumnMenu
      ) {
        if (!column.key) {
          throw new Error(
            "Column should have a key to support Sorting/Filtering",
          );
        }
        const sortOrder = sortInfo[key];
        const isSortActive = !!sortOrder;
        const isFilterActive = filterInfo[key]?.length > 0;
        const isFilterKeyActive = activeFilterKey === key;
        const isHoverOnThisColumn =
          activeColumnHoverKey === key || isFilterKeyActive;
        const isMenuVisible = isHoverOnThisColumn || visibleMenuKey === key;
        const columnMenuCommonProps: ACColumnMenuCommonProps = {
          onPopupVisibleChange: (visible) => {
            setVisibleMenuKey(visible ? key : undefined);
          },
        };
        return {
          ...omit(column, ["filters", "filterDropdown", "sorter"]),
          ellipsis: isUndefined(column.ellipsis) ? true : column.ellipsis,
          title: (
            <div
              data-key={key}
              data-test-id={
                column?.testId ||
                `column-${String(key ?? "unknown").toLowerCase()}`
              }
              onMouseOver={() => setActiveColumnHoverKey(key)}
              className={classNames(`ac-table-column-header-container`, {
                "has-sort-active": isSortActive,
                "has-filter-active":
                  isFilterActive ||
                  (isHoverOnThisColumn && column.filterDropdown),
                "has-sort": !!column.sorter,
                "has-menu-visible": isMenuVisible,
              })}
              data-type={sortOrder}
              data-sort-type={column.sortType || "default"}
              onClick={column.sorter ? _onSortChange : undefined}
            >
              <div className="ac-table-column-title-and-sort">
                <div className="ac-table-column-header-title">{title}</div>
                {(column.sorter && (isHoverOnThisColumn || isSortActive)) ||
                isFilterActive ? (
                  <div
                    data-test-id={`sort-${String(key ?? "unknown").toLowerCase()}`}
                    className={classNames(
                      "ac-table-column-sort-icon",
                      // isSortActive ? "sort-active" : undefined
                    )}
                  >
                    {(isSortActive || isHoverOnThisColumn) &&
                      column.sorter &&
                      (sortOrder === "ascend" ? (
                        <SortAscIcon />
                      ) : sortOrder === "descend" ? (
                        <SortDescIcon />
                      ) : columnSortType === "number" ? (
                        <SortDescIcon />
                      ) : (
                        <SortAscIcon />
                      ))}
                    {isFilterActive && <FilterIcon />}
                  </div>
                ) : null}
              </div>
              {isMenuVisible ? (
                column.customMenuRender ? (
                  column.customMenuRender(columnMenuCommonProps)
                ) : (
                  <ACColumnOptionDropdown
                    columnKey={key}
                    sortOrder={sortOrder}
                    hasSort={
                      !isUndefined(column.sorter) && column.sorter !== false
                    }
                    sortType={column.sortType}
                    showDropdown={column.showDropdown}
                    onSortChange={onSortChange}
                    showFilter={
                      column.filterDropdown
                        ? () => {
                            setActiveFilterKey(key);
                            setActiveFilterSelectedKeys(
                              filterInfo[key] ? filterInfo[key] : [],
                            );
                            setActiveFilterSelectedOperation(
                              filterOperationInfo[key]
                                ? filterOperationInfo[key]
                                : undefined,
                            );
                          }
                        : undefined
                    }
                    draggable={column.draggable}
                    columnKeys={draggableColumns}
                    columnOrderConfig={columnOrderConfig}
                    onColumnOrderChange={onColumnOrderChange}
                    onColumnVisibilityChange={onColumnVisibilityChange}
                    alwaysVisibleColumns={alwaysVisibleKeys}
                    dateFormatter={column.dateFormatter}
                    allowGroupBy={column.allowGroupBy}
                    groupByKey={column.groupByKey}
                    groupBy={groupBy}
                    onGroupByChange={onGroupByChange}
                    {...columnMenuCommonProps}
                  />
                )
              ) : null}
              {column.filterDropdown ? (
                <ACTrigger
                  offsetY={25}
                  trigger={"click"}
                  popup={
                    <div onClick={(e) => e.stopPropagation()}>
                      {isFilterKeyActive && isFunction(column.filterDropdown)
                        ? column.filterDropdown({
                            prefixCls: "",
                            selectedKeys: getActiveFilterSelectedKeys(),
                            setSelectedKeys: (keys: React.Key[]) => {
                              setActiveFilterSelectedKeys(
                                keys.map((key) => key as FilterValue),
                              );
                            },
                            setFilterOperation: (
                              type?: FilterOperationType,
                            ) => {
                              setActiveFilterSelectedOperation(type);
                            },
                            confirm: (closeMenu?: boolean) =>
                              applyFilter(
                                activeFilterKey,
                                getActiveFilterSelectedKeys(),
                                getActiveFilterSelectedOperation(),
                                closeMenu,
                              ),
                            close: () => {},
                            visible: isFilterKeyActive,
                            clearFilters: () =>
                              applyFilter(activeFilterKey, undefined),
                            filterLabelFormatter: column.filterLabelFormatter,
                          } as ACFilterDropdownProps)
                        : undefined}
                    </div>
                  }
                  onPopupVisibleChange={(visible) => {
                    setActiveFilterKey(visible ? key : undefined);
                    setActiveFilterSelectedKeys(
                      visible && filterInfo[key] ? filterInfo[key] : [],
                    );
                  }}
                  placement={"bottomRight"}
                  popupVisible={isFilterKeyActive}
                >
                  <span />
                </ACTrigger>
              ) : null}
            </div>
          ),
        };
      }
      return {
        ...column,
        title: (
          <div
            className={classNames("ac-table-column-header-container", {
              "align-title-right": column.align === "right",
            })}
          >
            {title && (
              <div className="ac-table-column-header-title ">{title}</div>
            )}
          </div>
        ),
        ellipsis: isUndefined(column.ellipsis) ? true : column.ellipsis,
      } as ACTableColumnType<Record>;
    });
  }, [
    columns,
    filterInfo,
    activeColumnHoverKey,
    activeFilterKey,
    getActiveFilterSelectedKeys,
    _onSortChange,
    applyFilter,
    setActiveFilterSelectedKeys,
    sortInfo,
    columnTitleRenderers,
    customizationHelpers,
    onSortChange,
    visibleMenuKey,
    groupBy,
    onGroupByChange,
    setActiveFilterSelectedOperation,
    filterOperationInfo,
    getActiveFilterSelectedOperation,
  ]);

  const [ref, dimensions] = useDimensions();
  const wrapperWidth = dimensions?.width ?? 0;
  const computedProps: TableProps<Record> = {};
  if (computeTableWidth) {
    let tableWidth = modifiedColumns.reduce((width, column) => {
      if (isNumber(column.width)) {
        return column.width + width;
      }
      throw new Error(
        `Cannot auto calculate table width as column ${column.key} doesn't have width as number`,
      );
    }, 0);
    if (rowSelection) {
      tableWidth += isNumber(rowSelection?.columnWidth)
        ? rowSelection.columnWidth
        : ROW_SELECTION_COLUMN_WIDTH;
    }
    if (expandable) {
      tableWidth += isNumber(expandable?.columnWidth)
        ? expandable.columnWidth
        : ROW_SELECTION_COLUMN_WIDTH;
    }
    // Add action/fill column when actionRender is provided or space available
    if (isFunction(actionRender) || tableWidth < wrapperWidth) {
      const MIN_ACTION_COLUMN_WIDTH = actionColumnWidth ?? 60; // Use custom width or default to 60px
      const hasActionRender = isFunction(actionRender);
      const hasScroll = tableWidth >= wrapperWidth;

      // Determine column width based on whether we have actionRender and scroll
      // When no scroll, use the larger of MIN_ACTION_COLUMN_WIDTH or remaining space
      const columnWidth = hasActionRender
        ? hasScroll
          ? MIN_ACTION_COLUMN_WIDTH
          : Math.max(MIN_ACTION_COLUMN_WIDTH, wrapperWidth - tableWidth)
        : wrapperWidth - tableWidth;

      modifiedColumns = [
        ...modifiedColumns,
        {
          title: "",
          width: columnWidth,
          render: hasActionRender ? (record) => actionRender(record) : () => "",
          align: "right",
          onHeaderCell: () => ({ style: { pointerEvents: "none" } }),
          ...(hasActionRender && hasScroll && { fixed: "right" }), // Only fix right when there's actual horizontal scroll
        } as ACTableColumnType<Record>,
      ];

      tableWidth =
        hasActionRender && hasScroll
          ? tableWidth + MIN_ACTION_COLUMN_WIDTH
          : wrapperWidth;
    }
    computedProps.style = {
      ...restProps.style,
      width: tableWidth,
    };
    if (autoEnableScroll) {
      computedProps.scroll = { x: tableWidth, ...restProps.scroll };
    }
  }

  useEffect(() => {
    if (getTableWidth && computedProps.style?.width) {
      getTableWidth(computedProps.style.width as number);
    }
  }, [getTableWidth, computedProps.style?.width]);

  return (
    <div ref={(r) => ref(r!)}>
      <Table<Record>
        {...restProps}
        {...computedProps}
        data-test-id={testId || "ac-table"}
        className={classNames(restProps.className, {
          "ac-table-no-header": restProps.showHeader === false,
          "ac-table-no-pagination": pagination === false,
          "ac-table-full-page": fullPage,
          "ac-table-with-scroll-shadow": rightScrollShadow,
          "ac-table-footer-size-sm": footerSize === "sm",
        })}
        prefixCls="ac-table"
        columns={modifiedColumns}
        size="middle"
        pagination={
          pagination !== false
            ? { ...pagination, className: "display-none" }
            : pagination
        }
        rowClassName={(...args) => {
          return classNames(
            isFunction(rowClassName) ? rowClassName(...args) : rowClassName,
            `ac-table-row-${args[1] % 2 === 0 ? "even" : "odd"}`,
          );
        }}
        onHeaderRow={(...args) => {
          const props = isFunction(onHeaderRow) ? onHeaderRow(...args) : {};
          return {
            onMouseLeave: (e) => {
              setActiveColumnHoverKey(undefined);
              (props.onMouseLeave || noop)(e);
            },
            ...props,
          };
        }}
        expandable={
          expandable
            ? {
                ...expandable,
                expandIcon: expandable.expandIcon
                  ? expandable.expandIcon
                  : ({ expanded, onExpand, record }) => {
                      return (
                        <span
                          onClick={(e) => onExpand(record, e)}
                          style={{
                            fontSize: 24,
                            margin: -9,
                            cursor: "pointer",
                          }}
                        >
                          {expanded ? (
                            <ChevronDownIcon />
                          ) : (
                            <ChevronRightIcon />
                          )}
                        </span>
                      );
                    },
                columnWidth:
                  expandable.columnWidth || ROW_SELECTION_COLUMN_WIDTH,
              }
            : undefined
        }
        rowSelection={
          rowSelection
            ? {
                ...rowSelection,
                columnWidth:
                  rowSelection.columnWidth || ROW_SELECTION_COLUMN_WIDTH,
              }
            : undefined
        }
        sticky={
          sticky
            ? {
                getContainer: stickyGetContainer,
                ...(sticky === true ? {} : sticky),
              }
            : false
        }
        dataSource={dataSource}
      />
    </div>
  );
}

const ROW_SELECTION_COLUMN_WIDTH = 42;

const stickyGetContainer = () =>
  document.getElementsByClassName("app-content-wrapper")[0] as HTMLElement;
